import React, { useState, useEffect } from "react";
import axiosInstance from '../../../utils/axiosInstance';
import "./Bar.css";
import { FaGlassWhiskey, FaWineGlass, FaBeer, FaWineBottle, FaGlassMartini, FaGlassCheers, FaBan } from 'react-icons/fa';
import { GiBeerHorn } from 'react-icons/gi';

function Bar() {
  const [drinkItems, setDrinkItems] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    async function fetchDrinkItems() {
      try {
        const response = await axiosInstance.get('/drinks/all', { timeout: 100000 });
        // Filter items to only include those visible to the customer
        const visibleItems = response.data.filter(item => item.visibleToCustomer);
        setDrinkItems(visibleItems);
      } catch (error) {
        // Error handling without console.log
      }
    }
    fetchDrinkItems();
  }, []);

  const formatPrice = (price) => `$${price.toFixed(2)}`;

  const groupByCategory = (items) => {
    const groupedItems = items.reduce((groupedItems, item) => {
      (groupedItems[item.category] = groupedItems[item.category] || []).push(item);
      return groupedItems;
    }, {});

    // Define the desired order of categories
    const categoryOrder = ['Beer', 'Wine', 'Mead', 'Cider', 'Cocktail', 'Spirit', 'Seltzer', 'NA'];
    
    // Create a new object with sorted categories
    const sortedGroupedItems = {};
    categoryOrder.forEach(category => {
      if (groupedItems[category]) {
        sortedGroupedItems[category] = groupedItems[category];
      }
    });

    return sortedGroupedItems;
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'NA':
        return 'Non-Alcoholic Drinks';
      case 'Beer':
        return 'Beer';
      case 'Wine':
        return 'Wine';
      case 'Mead':
        return 'Meade';
      case 'Cider':
        return 'Cider';
      case 'Cocktail':
        return 'Cocktails';
      case 'Spirit':
        return 'Spirits';
      case 'Seltzer':
        return 'Seltzers';
      default:
        return category;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'NA':
        return (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <FaGlassMartini style={{ position: 'relative', zIndex: 1 }} />
            <FaBan style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'red', zIndex: 2 }} />
          </div>
        );
      case 'Beer':
        return <FaBeer />;
      case 'Wine':
        return <FaWineGlass />;
      case 'Mead':
        return <GiBeerHorn />;
      case 'Cider':
        return <FaWineBottle />;
      case 'Cocktail':
        return <FaGlassMartini />;
      case 'Spirit':
        return <FaGlassWhiskey />;
      case 'Seltzer':
        return <FaGlassCheers />;
      default:
        return <FaGlassWhiskey />;
    }
  };

  const scrollToCategory = (category) => {
    const element = document.getElementById(`category-${category}`);
    if (element) {
      const titleElement = element.querySelector('h2');
      if (titleElement) {
        // Increased offset to prevent scrolling too far past the section header
        const headerOffset = 200;
        const elementPosition = titleElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        // Check if we're already at the bottom of the page
        const isAtBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight;
        
        if (!isAtBottom) {
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  const toggleDescription = (itemId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const toggleRow = (itemId) => {
    setExpandedRows(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const groupedItems = groupByCategory(drinkItems);

  const renderMobileRow = (item) => (
    <div 
      className={`mobile-drink-row ${expandedRows[item._id] ? 'expanded' : ''}`}
      onClick={() => toggleRow(item._id)}
    >
      <div className="mobile-drink-main">
        <div className="mobile-drink-left">
          <div className="mobile-drink-name">
            {item.name}
            {item.abv && <span className="drink-abv">, {item.abv}%</span>}
            {item.isFavorite && <span className="favorite-stamp">Favorite</span>}
          </div>
          {!expandedRows[item._id] && (
            <div className="mobile-drink-prompt">
              Click to see more details
            </div>
          )}
        </div>
        <div className="mobile-drink-details">
          <div className="mobile-drink-detail">
            <span className="detail-label">Size:</span>
            <span className="detail-value">{item.size} oz</span>
          </div>
          <div className="mobile-drink-detail">
            <span className="detail-label">Price:</span>
            <span className="detail-value">{formatPrice(item.price)}</span>
          </div>
        </div>
      </div>
      {expandedRows[item._id] && (
        <div className="mobile-drink-expanded">
          <div className="mobile-drink-info">
            <div className="mobile-drink-label">Variety:</div>
            <div className="mobile-drink-value">{item.variety}</div>
          </div>
          <div className="mobile-drink-info">
            <div className="mobile-drink-label">Source:</div>
            <div className="mobile-drink-value">{item.source || 'N/A'}</div>
          </div>
          <div className="mobile-drink-info">
            <div className="mobile-drink-label">Description:</div>
            <div 
              className={`mobile-drink-description ${expandedDescriptions[item._id] ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleDescription(item._id);
              }}
            >
              {item.description}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bar-menu">
      <h1>Bar Menu</h1>
      <div className="category-nav">
        {Object.keys(groupedItems).map((category) => (
          <button
            key={category}
            className="category-nav-button"
            onClick={() => scrollToCategory(category)}
            title={getCategoryTitle(category)}
          >
            <div className="category-icon-wrapper">
              {getCategoryIcon(category)}
            </div>
            <span className="category-label">{getCategoryTitle(category)}</span>
          </button>
        ))}
      </div>
      {Object.keys(groupedItems).map((category) => (
        <div key={category} id={`category-${category}`} className="category-section">
          <h2>{getCategoryTitle(category)}</h2>
          {isMobile ? (
            <div className="mobile-drinks-container">
              {groupedItems[category].map((item) => (
                <div key={item._id}>
                  {renderMobileRow(item)}
                </div>
              ))}
            </div>
          ) : (
            <div className="bar-menu-table-container">
              <table className="bar-menu-table">
                <thead>
                  <tr>
                    <th className="bar-menu-header">Name</th>
                    <th className="bar-menu-header">Source</th>
                    <th className="bar-menu-header">ABV</th>
                    <th className="bar-menu-header">Size (oz)</th>
                    <th className="bar-menu-header">Description</th>
                    <th className="bar-menu-header">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedItems[category].map((item) => (
                    <tr key={item._id} className="bar-menu-row">
                      <td className="bar-menu-cell">
                        {item.name}
                        {item.isFavorite && <span className="favorite-stamp">Favorite</span>}
                        <div className="drink-subtext">{item.variety}</div>
                        {item.size && <div className="drink-subtext">Size: {item.size} oz</div>} 
                      </td>
                      <td className="bar-menu-cell">{item.source || 'N/A'}</td>
                      <td className="bar-menu-cell">{item.abv ? `${item.abv}%` : 'N/A'}</td>
                      <td className="bar-menu-cell">{item.size}</td>
                      <td className="bar-menu-cell">
                        <div 
                          className={`description-content ${expandedDescriptions[item._id] ? 'expanded' : ''}`}
                          onClick={() => toggleDescription(item._id)}
                        >
                          {item.description}
                        </div>
                      </td>
                      <td className="bar-menu-cell">{formatPrice(item.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default Bar;
